import { z } from 'zod';
import { uuidSchema, isoDateTimeSchema, timestampedSchema } from '../schemas/common';

// Production: the season container (e.g. "Spring Recital 2027").
export const productionInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  season: z.string().trim().min(1).max(80),
  venue_name: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ProductionInput = z.infer<typeof productionInputSchema>;

export const productionSchema = timestampedSchema.extend({
  name: z.string(),
  season: z.string(),
  venue_name: z.string().nullable(),
  venue_address: z.unknown().nullable(),
  notes: z.string().nullable(),
  deleted_at: isoDateTimeSchema.nullable(),
});
export type Production = z.infer<typeof productionSchema>;

// Show: one performance within a production.
export const showInputSchema = z.object({
  production_id: uuidSchema,
  name: z.string().trim().min(1).max(120),
  performance_at: isoDateTimeSchema,
  intermission_after_sequence: z.number().int().positive().nullable().optional(),
  intermission_minutes: z.number().int().min(0).max(60).default(15),
  default_gap_seconds: z.number().int().min(0).max(600).default(60),
  // Sprint 11 — per-show ticket sale controls.
  max_tickets_per_purchase: z.number().int().min(1).max(200).nullable().optional(),
  ticket_sales_open: z.boolean().default(true),
  ticket_sales_open_at: isoDateTimeSchema.nullable().optional(),
});
export type ShowInput = z.infer<typeof showInputSchema>;

export const showSchema = timestampedSchema.extend({
  production_id: uuidSchema,
  name: z.string(),
  performance_at: isoDateTimeSchema,
  intermission_after_sequence: z.number().int().nullable(),
  intermission_minutes: z.number().int(),
  default_gap_seconds: z.number().int(),
  max_tickets_per_purchase: z.number().int().nullable(),
  ticket_sales_open: z.boolean(),
  ticket_sales_open_at: isoDateTimeSchema.nullable(),
  deleted_at: isoDateTimeSchema.nullable(),
});
export type Show = z.infer<typeof showSchema>;

// Sale-status view (Sprint 11). One row per show; counts available seats.
export const showSalesStatusSchema = z.object({
  show_id: uuidSchema,
  production_id: uuidSchema,
  name: z.string(),
  performance_at: isoDateTimeSchema,
  max_tickets_per_purchase: z.number().int().nullable(),
  ticket_sales_open: z.boolean(),
  ticket_sales_open_at: isoDateTimeSchema.nullable(),
  on_sale: z.boolean(),
  seats_available: z.number().int().nonnegative(),
});
export type ShowSalesStatus = z.infer<typeof showSalesStatusSchema>;

// Dance: choreography piece within a production.
export const danceInputSchema = z.object({
  production_id: uuidSchema,
  name: z.string().trim().min(1).max(200),
  style: z.string().trim().min(1).max(60),
  level: z.string().trim().max(40).optional(),
  choreographer_id: uuidSchema.nullable().optional(),
  music_title: z.string().trim().max(200).optional(),
  music_artist: z.string().trim().max(200).optional(),
  length_seconds: z.number().int().min(15).max(1800).default(180),
  notes: z.string().trim().max(2000).optional(),
});
export type DanceInput = z.infer<typeof danceInputSchema>;

export const danceSchema = timestampedSchema.extend({
  production_id: uuidSchema,
  name: z.string(),
  style: z.string(),
  level: z.string().nullable(),
  choreographer_id: uuidSchema.nullable(),
  music_title: z.string().nullable(),
  music_artist: z.string().nullable(),
  length_seconds: z.number().int(),
  notes: z.string().nullable(),
  deleted_at: isoDateTimeSchema.nullable(),
});
export type Dance = z.infer<typeof danceSchema>;

// Lineup item: a dance's position in a show's running order.
export const showLineupSchema = z.object({
  show_id: uuidSchema,
  dance_id: uuidSchema,
  sequence_index: z.number().int().positive(),
  computed_start_time: isoDateTimeSchema.nullable(),
});
export type ShowLineup = z.infer<typeof showLineupSchema>;

// Cast assignment.
export const castAssignmentInputSchema = z.object({
  dance_id: uuidSchema,
  dancer_id: uuidSchema,
  role: z.string().trim().max(80).optional(),
});
export type CastAssignmentInput = z.infer<typeof castAssignmentInputSchema>;

export const castAssignmentSchema = z.object({
  id: uuidSchema,
  created_at: isoDateTimeSchema,
  dance_id: uuidSchema,
  dancer_id: uuidSchema,
  role: z.string().nullable(),
});
export type CastAssignment = z.infer<typeof castAssignmentSchema>;
